import { t } from '@lingui/core/macro';
import { getLocale } from 'i18n/locale';
import { EExperience, fullStackSessionSchema, TPrString } from 'data/schema';
import * as z from 'zod';
import { sendApiRequest } from 'utils/network';
import { debounce } from 'utils/functions';
import { normalizeModelRevision, supportsModelRevision } from 'utils/modelRevision';
import { getCurrentUrlParameter, safeUrl } from 'utils/url';
import type { ICarDetails, IVtpDetails, IVtpPhoto } from 'lambda/vehicle-data/types';
import {
  broadcastingConnection,
  broadcastingConnectionId,
  hasReadyVoipConnection,
  initSessionBroadcasting,
  initWebRtc,
  voipRtcConnection,
} from 'utils/ui/rtc';
import { myCameraStream } from 'state/video';
import { sendSessionAnalyticsEvent } from 'utils/ui/analytics';
import { ESessionEvents, TCarSelectionMethod } from 'data/analytics';
import { isValidPrString, isValidSessionID } from 'utils/session';
import { CustomerFacingError } from 'utils/ui/errors';
import { EErrorCode } from 'config/errors-codes';
import { user } from 'utils/auth/user';
import { resetLaunchedConfigurator } from 'utils/ui/webconfigurator';
import type { models } from '@org-avp/device-registry';
import { isObject, jsonSafeParse } from 'utils/objects';
import type { E3DRuntime, ITenantDefaults } from 'utils/runtime';
import { getStageOrigin } from 'utils/ui/stage';
import { initEvents } from './session/events';
import { IntegrationType, integrationType } from 'utils/ui/app';
import { tenantConfig } from 'utils/tenant';
import { startBroadcast, stopBroadcast, resumeBroadcast } from 'utils/network/nerdManager';
import { SessionState } from './session/computed';
import { getVoipSlotId, getVoipTabId } from 'utils/voipTabId';
import {
  clearVoipClaim,
  clearVoipRelease,
  consumeReassertBudgetForRepair,
  getVoipClaimGen,
  nextVoipClaimGen,
  registerVoipClaim,
  registerVoipRelease,
  resolveVoipClaimAction,
  voipMasterTabId,
} from './session/voipClaim';
import {
  clearVoipClaimPending,
  clearVoipClaimPendingOfOtherSession,
  setVoipClaimPending,
} from './session/voipClaimPending';
import { isDebugMode } from 'utils/ui/debug';
import { isSharingOptionBlockedByComparison } from 'utils/sharingOptions';
import logger from 'utils/log';
import { showConfirmDialog } from 'state/confirm-dialog';
import { addVehicle } from './session/vehicleRegistry';
import { clearDirty, getDirtyKeys } from './session/dirtyKeys';
import { getLastSeenVersion, seedRemoteVersion } from './session/remoteVersion';
import { resetSessionSyncState } from './session/resetSessionSyncState';
import { repairPublicSession } from './session/readRepair';
import { scheduleDirtyFlush } from './session/dirtyFlush';
import { ApiError } from 'utils/errors';
import { markSessionInherited, clearInheritedSession, isInheritedSession } from './session/inheritedSession';
import { clearStreamHost, isStreamHostForSession } from './session/streamHostClaim';
import { isOwnDeviceBinding, isBindingStale } from 'utils/devices';
import type { DeviceLike } from 'utils/devices';
import { localAveTabId } from 'utils/localAveTabId';

export { localAveTabId };

/**
 * Global configuration state.
 * The app should use configuration params from here, only.
 * We can replace data source (e.g. initial data) with a remote one, for example.
 * This session data is shared between the dealer and customers.
 */

export const sharedSessionSchema = fullStackSessionSchema
  .extend({
    id: z.string().optional(),
    partnerToken: z.string().default(process.env.PARTNER_TOKEN || 'd88f48fd-6463-4516-9873-8ec91f4d8d34'),
    locale: z.string().default(getLocale()),
    sharingUri: z.string().default(process.env.UI_ORIGIN ?? ''),
    carSelectionMethod: z.union([z.custom<TCarSelectionMethod>(), z.literal('')]).default(''),
    customerAllowance: z.boolean().default(false),
    hasTimedOut: z.boolean().default(false),
    // flag that indicates we are already synchronizing the session with the server
    // useful to avoid repeated calls to the server when session id has not been set yet
    syncStarted: z.boolean().default(false),
  })
  // strip out values that don't belong to this schema and shouldn't be sent to the server
  .strip();

/**
 * Customer session schema excludes dealer-only fields — the live-stream identifiers
 * (aveSessionId/aveChannelName) plus every vehicle-registry-derived field (AVS-4400).
 * A customer only ever RECEIVES vehicle selection/colour changes from the dealer via
 * broadcast; it never legitimately originates one. Omitting these here means a customer's
 * PUT — real write or `emitOnly` broadcast alike — can never carry a stale echo of an
 * earlier vehicle back to the dealer (the concrete AVS-4400 defect), and the server-side
 * partial update (`ServiceAbstract.update` with `partialUpdate: true`) leaves the
 * corresponding DB columns untouched rather than resetting them, so this is safe for
 * real writes too. The omitted set mirrors `sessionVehicleSchema` (the same schema
 * `vehicleRegistry.ts` uses to model one registry entry) plus `vehicles`/`activeVehicleId`.
 * `showroomConfigId` is omitted too: a customer only receives the dealer's Showroom config-id
 * and never originates it (AVS-4477). `modelRevision` is omitted for the same reason: it is
 * dealer-owned vehicle data that travels with `prString` (AVS-4481).
 */
export const customerSessionSchema = fullStackSessionSchema.omit({
  aveSessionId: true,
  aveChannelName: true,
  vehicles: true,
  activeVehicleId: true,
  prString: true,
  carImages: true,
  carImagesStatus: true,
  carDetails: true,
  wltpText: true,
  carStatus: true,
  carDisplayName: true,
  vinOrMarketingCode: true,
  dgaLink: true,
  efficiencyClass: true,
  isVtp: true,
  vtpDetails: true,
  vtpPhotos: true,
  okapiCode: true,
  showroomConfigId: true,
  modelRevision: true,
});

/**
 * This schema is only for the data that should NOT be exchanged with the server and other clients
 */
export const userSessionSchema = sharedSessionSchema.extend({
  streamFullScreen: z.boolean().default(false),
  snapshot: z.string().default('{}'),
  ratingCompleted: z.boolean().default(false),
  offline: z.boolean().default(false),
});

export type IPublicSession = z.infer<typeof userSessionSchema>;

/**
 * Global connectionCallback registered via E3DRuntime.
 * Used as a fallback when no per-call or WC attribute callback is provided.
 */
let globalConnectionCallback: IShareOptions['connectionCallback'] | undefined;

export function setGlobalConnectionCallback(cb: IShareOptions['connectionCallback'] | undefined | null) {
  globalConnectionCallback = cb ?? undefined;
}

export function getGlobalConnectionCallback() {
  return globalConnectionCallback;
}

let state: SessionState;

export function userSession() {
  if (!state) {
    state = new SessionState(sessionInitializationDefaults());
    initEvents(state);
  }

  return state;
}

export const initSession = async (sessionId?: string) => {
  if (!sessionId || (!isValidSessionID(sessionId) && !isValidPrString(sessionId))) {
    throw new CustomerFacingError(EErrorCode.INVALID_SESSION_ID);
  }

  try {
    const sessionData = await loadPublicSession(sessionId);
    initSessionBroadcasting();
    // If this is a broadcast session and it's active, resume the broadcast
    if (
      sessionData &&
      sessionData.sharingType === 'broadcast' &&
      isSessionActive(sessionData) &&
      sessionData.isShared
    ) {
      const snapshot = sharingSnapshot();
      if (!Object.keys(snapshot).length) {
        userSession().setValues({
          snapshot: JSON.stringify(sessionData),
        });
      }
      resumeBroadcast();
    }
    userSession().emit('session:resume', sessionData);
  } catch {
    throw new CustomerFacingError(EErrorCode.CANT_LOAD_SESSION);
  }
};

/**
 * Start sharing session data via the server
 */
async function registerSessionOnServer(sessionValues: Partial<IPublicSession> = {}) {
  if (!user.isDealer) {
    return;
  }

  // Block API operations when offline
  if (userSession().get('offline')) {
    logger.warn('Session not registered: offline mode is enabled');
    return;
  }

  const initialSession = userSession().get();

  if (initialSession.syncStarted) {
    // already in progress
    return;
  }

  userSession().setValues(
    {
      ...initialSession,
      ...sessionValues,
      userPseudonym: user.userPseudonym,
      dealerId: user.dealerId,
      dealerName: user.name,
      dealerEmail: user.email,
      dealerPhone: user.phoneNumber,
      dealerPhoto: user.publicPicture,
      syncStarted: true,
      offline: false,
    },
    { emit: false }
  );

  // session already exists so we are just updating the existing one
  // (e.g. aveSessionId arrived from the showroom after the initial session_created
  // event was sent — emit session_updated so analytics captures the mmToken
  // without inflating the session_created count).
  if (initialSession.id) {
    if (sessionValues.aveSessionId || sessionValues.aveChannelName) {
      sendSessionAnalyticsEvent(ESessionEvents.SESSION_UPDATED, userSession().data);
    }
    return;
  }

  // AVS-4353: `syncStarted` is set above (before the request) so concurrent callers dedupe on
  // the early return at the top of this function. It was never cleared when the POST failed,
  // so a single transient network error left the flag stuck true and every later registration
  // attempt silently no-opped for the rest of the session — the dealer could not recover
  // without a full reset. The resolved path clears it via the `sessionValues` spread below;
  // only the rejection path leaked, so clear it here and let the error propagate unchanged.
  const newSession = await sendApiRequest<IPublicSession>('/sessions', {
    method: 'POST',
    body: userSession().get(),
  }).catch((error) => {
    userSession().setValues({ syncStarted: false }, { emit: false });
    throw error;
  });

  userSession().markRequirementReady('stateSynced');

  userSession().setValues({
    ...sessionValues,
    ...newSession,
  });

  // make sure no race condition leads to overhead PUT calls
  updatePublicSession.cancel();

  return newSession;
}

export function updatePublicSessionNow({
  keepAlive,
  data,
  omitKeys,
  emitOnly = false,
  updateEverything = false,
  forceFullBody = false,
  retryAfterConflict = false,
}: {
  keepAlive?: boolean;
  data?: Partial<IPublicSession>;
  /**
   * Keys to drop from the outgoing body, rebuilt from LIVE state at fire time (AVS-4399).
   *
   * The clobber guard needs to suppress a key without freezing everything else. Passing a
   * pre-built `data` snapshot did both: by the time the debounce fired, an inbound broadcast may
   * have been applied, and the snapshot carried the pre-broadcast values back to the server. CAS
   * cannot catch that — `clientVersion` is read at fire time, so `expectedVersion` matches the
   * version the broadcast just seeded and the stale write is accepted.
   */
  omitKeys?: (keyof IPublicSession)[];
  emitOnly?: boolean;
  updateEverything?: boolean;
  /**
   * Keep this write off the delta path without touching broadcast semantics (AVS-4399).
   *
   * `updateEverything` also forces a full body, but it changes what the server broadcasts (it is
   * what stops `prString` being stripped), so it is not a free way to ask for one. The re-send
   * driver needs the full body for its own reason: after an unconfirmed write we do not know which
   * keys the server actually took, so re-sending only the dirty ones would be a guess.
   */
  forceFullBody?: boolean;
  /** Internal: set by the CAS conflict handler on its single retry — never pass from feature code. */
  retryAfterConflict?: boolean;
} = {}): Promise<IPublicSession | undefined> | undefined {
  // Explicit `data` (e.g. endSession / syncSnapshot) carries keys that were never marked dirty, so
  // it must always send the FULL body — a delta keyed on the dirty set would drop them (AVS-4330).
  const isExplicitData = data !== undefined;
  const effectiveData = data ?? userSession().get();
  const id = effectiveData?.id;
  if (!id) return;

  // Block API operations when offline
  if (userSession().get('offline')) {
    logger.warn('Session not updated: offline mode is enabled');
    return;
  }

  // Customers are not intended to update properties of the sharedSessionSchema
  // They only need to be able update properties of the internalSessionSchema
  const strippedData =
    user.type === 'customer'
      ? customerSessionSchema.safeParse(effectiveData)
      : sharedSessionSchema.safeParse(effectiveData);

  if (!strippedData.success) {
    // gracefully fail to update the session if some of the data is not there
    return;
  }

  // Keys this request is responsible for (a snapshot). Only these are cleared on success. A key
  // first dirtied AFTER this snapshot is not in the list and survives; a snapshot key re-edited
  // mid-flight cannot be distinguished (Set-based) and is cleared — a lost-update risk only under
  // deltaSessionSync (Gate I9), harmless under full-body PUTs which re-send the whole session.
  const inflightKeys = [...getDirtyKeys()];
  // Our last-seen version. The server echoes it onto emitOnly broadcasts (which never touch
  // the DB and so have no version of their own) so receivers can still gate them.
  const clientVersion = getLastSeenVersion();

  // Delta sync (AVS-4330, flag-gated, default OFF): send only the changed (dirty) keys instead of
  // the whole object. The server writes partial bodies correctly (SessionService.updateAndEmit →
  // partialUpdate). Full body is always used for updateEverything (WC 2D / local AVE need the whole
  // snapshot), emitOnly, an explicit-data caller, or when nothing is dirty.
  const fullBody = strippedData.data as Record<string, unknown>;
  // Safe to mutate: `strippedData.data` is a fresh object produced by the schema parse, not live state.
  omitKeys?.forEach((key) => delete fullBody[key as string]);

  const useDeltaBody =
    !isExplicitData &&
    !emitOnly &&
    !updateEverything &&
    !forceFullBody &&
    inflightKeys.length > 0 &&
    tenantConfig().isAllowed('deltaSessionSync');
  const body: Record<string, unknown> = useDeltaBody
    ? inflightKeys.reduce<Record<string, unknown>>((acc, key) => {
        const k = key as string;
        if (k in fullBody) acc[k] = fullBody[k];
        return acc;
      }, {})
    : fullBody;

  // Say "this car has no gallery" out loud (AVS-4399).
  //
  // `carImages: undefined` survives the schema parse as an own key but disappears in
  // JSON.stringify, while `carImagesStatus` ships regardless. The receiver applies only the keys a
  // payload actually contains, so it was told the gallery was unset while keeping the PREVIOUS
  // car's images — the "Vehicle Image Coming Soon" panel over a stale gallery.
  //
  // Normalised on the OUTGOING body, not on `fullBody`: under `deltaSessionSync` the body is
  // rebuilt from the dirty keys alone, so a placeholder written to `fullBody` is dropped whenever
  // `carImagesStatus` is dirty and `carImages` is not — handing the receiver the very
  // missing-gallery payload this exists to remove. `prString` is read from `fullBody` because a
  // delta need not carry it.
  //
  // Gated on the STATUS VALUE, never on key presence: `carImagesStatus` carries `.default('unset')`,
  // so it survives the schema parse on every single body and `'carImagesStatus' in body` is always
  // true. That reduced the guard to "no gallery yet", which is also what a fetch in flight looks
  // like, and fabricating an empty gallery there is worse than the bug it fixes — it persists
  // `carImages.prString === prString` with no items, which `initCarImagesAutoUpdate` will neither
  // refetch (its guard is `carImages.prString !== prString`) nor self-heal (that needs
  // `items.length > 0`). Only `unset` means "this car has no gallery"; `loading` means ask later,
  // and `ready` with no `carImages` is the legitimate VTP-photos-only vehicle.
  //
  // Only the wire shape changes. Local state deliberately keeps `undefined`: the 2D auto-update
  // decides whether to refetch by comparing `carImages.prString` against `prString`, and a stored
  // empty gallery keyed to the current car would convince it there is nothing to fetch.
  if (body.carImagesStatus === 'unset' && !body.carImages && typeof fullBody.prString === 'string') {
    body.carImages = { prString: fullBody.prString, items: [] };
  }

  // Compare-and-set (AVS-4382): full-body PUTs are CAS-protected once a version is known; delta stays gated by deltaSessionSync.
  const useCas = !emitOnly && clientVersion !== undefined;

  return sendApiRequest<IPublicSession>(
    `/sessions/${id}?emitOnly=${emitOnly}&emitter=${broadcastingConnectionId ?? ''}&updateEverything=${updateEverything}` +
      (clientVersion === undefined ? '' : `&clientVersion=${clientVersion}`) +
      (useCas ? `&expectedVersion=${clientVersion}` : ''),
    {
      method: 'PUT',
      keepalive: keepAlive,
      body,
      headers: {
        'X-User': user.userPseudonym,
      },
      throwOnStaleWrite: useCas,
    }
  )
    .then((updated) => {
      // A rejected stale write resolves to undefined (network/index.ts swallows the 409), so an
      // absent response means "not confirmed": keep those keys dirty for a later attempt rather
      // than dropping the edit. Only a real response clears them, and only the keys this
      // request carried.
      //
      // An emitOnly response proves nothing was persisted (AVS-4399): SessionService.updateAndEmit
      // returns undefined for it, and the lambda answers `LambdaResponse.success(result ?? body)` —
      // it echoes the client's own body back. That echo is truthy, so clearing on it dropped the
      // dirty snapshot for keys DynamoDB never received, costing the edit both its protection at
      // the remote-apply chokepoint and any chance of being re-sent.
      if (updated) {
        if (!emitOnly) {
          clearDirty(inflightKeys);
        }
        // `version` is server-set and lives on the internal schema, so it is not part of
        // IPublicSession — read it off the raw response.
        seedRemoteVersion((updated as { version?: number }).version);
      }
      // Anything still dirty was not confirmed by this response. Hand it to the re-send driver so
      // it cannot latch for the life of the session (AVS-4399).
      if (getDirtyKeys().size > 0) {
        scheduleDirtyFlush();
      }
      return updated;
    })
    .catch(async (error) => {
      const isStaleWrite = error instanceof ApiError && error.code === EErrorCode.SESSION_STALE_WRITE;
      // A network failure leaves the edit unconfirmed exactly like an unacknowledged write does;
      // the driver owns the retry either way. The error still propagates to the caller.
      if (!isStaleWrite) {
        if (getDirtyKeys().size > 0) {
          scheduleDirtyFlush();
        }
        throw error;
      }
      // Second conflict, or an explicit body we cannot rebuild from current state: fall back to
      // the legacy keep-dirty semantics — a later write or read-repair converges.
      //
      // Keeping keys dirty is only safe while something is still going to re-send them, and this
      // branch is terminal: it resolves the outer promise from inside `.catch`, so the `.then`
      // above — the only other place the driver is armed — never runs. Without this the dirty set
      // latches for the life of the session on a double CAS conflict, which is the exact failure
      // AVS-4399 exists to break.
      if (retryAfterConflict || isExplicitData) {
        if (getDirtyKeys().size > 0) {
          scheduleDirtyFlush();
        }
        return undefined;
      }
      // Rebase-and-retry: pull the current record (dirty-key protection keeps our pending edits),
      // then resend once from the refreshed local state with the refreshed expectedVersion.
      logger.info('[Session] Stale write rejected — rebasing on server state and retrying once');
      await repairPublicSession();
      return updatePublicSessionNow({ keepAlive, emitOnly, updateEverything, retryAfterConflict: true });
    });
}

/**
 * Union of two `omitKeys` lists, or undefined when neither call asked for a suppression.
 *
 * `omitKeys` is the only debounce argument whose meaning is "withhold this", so it is the only one
 * that cannot be resolved by picking a winner: latest-wins on a suppression silently un-suppresses.
 */
function mergeOmitKeys(
  a: (keyof IPublicSession)[] | undefined,
  b: (keyof IPublicSession)[] | undefined
): (keyof IPublicSession)[] | undefined {
  if (!a && !b) {
    return undefined;
  }
  return [...new Set([...(a ?? []), ...(b ?? [])])];
}

// AVS-4382 follow-up: an emitOnly broadcast (e.g. an index-only session emission whose keys live
// outside sharedSessionSchema) sharing the same 1s debounce window as a pending real write must
// never downgrade that write to broadcast-only — the eventual PUT would skip both the DB write
// and CAS (useCas excludes emitOnly), silently dropping a change like a car swap. So a real pending
// call wins over an emitOnly successor; every other combination (real->real, emitOnly->emitOnly,
// emitOnly->real) is plain latest-wins.
//
// Whichever side wins, `omitKeys` is carried across from BOTH (AVS-4399, PR #1791 review). Picking
// a winner outright dropped the loser's suppression in both directions: the clobber guard could
// fire on the emission that lost, and the single PUT the two collapse into would then carry the
// empty `aveSessionId` the guard existed to withhold, tearing the live stream down. A suppression
// is not a value to choose between, it is a constraint both callers have to keep.
//
// `data` deliberately does NOT merge. Injecting `next.data` into a pending call that had none
// would flip `isExplicitData` and skip the CAS-conflict rebase-and-retry, and taking `next`
// wholesale would drop an explicit body the pending caller supplied. syncToServer no longer passes
// `data` at all (it passes `omitKeys`), so the remaining `data` callers are endSession and
// syncSnapshot, which do not share a window with anything.
export const updatePublicSession = debounce(updatePublicSessionNow, 1000, {
  mergeArgs: (pendingArgs, nextArgs) => {
    const pending = pendingArgs[0];
    const next = nextArgs[0];
    // Real callers (syncToServer's relevant-keys path) never pass `emitOnly` explicitly — it
    // defaults to false inside updatePublicSessionNow itself — so a pending call is "real" when
    // its emitOnly is anything other than `true`, not only when it is literally `false`.
    const pendingIsReal = pending?.emitOnly !== true;
    const nextIsEmitOnly = next?.emitOnly === true;
    const winner = pendingIsReal && nextIsEmitOnly ? pendingArgs : nextArgs;

    const omitKeys = mergeOmitKeys(pending?.omitKeys, next?.omitKeys);
    if (!omitKeys) {
      return winner;
    }
    // Tuple, not array: `mergeArgs` returns the debounce's own `Parameters<T>`.
    return [{ ...winner[0], omitKeys }] as typeof winner;
  },
});

const sessionDataCache = new Map<string, Promise<IPublicSession | null>>();

// AVS-4080: Internal fetch helper shared by the public `getSessionData` and the route-loader
// path (`loadPublicSession`). The `suppressToast` flag is private: internal callers that own
// their own error UI (route loaders backed by ErrorBoundary / the deleted-session dialog)
// opt into suppression, while the public `getSessionData` (exposed via `E3DRuntime`) keeps
// the standard toast/`reportError` pipeline integrators rely on.
//
// Cache note: a single resolved promise is reused across callers regardless of which
// suppression mode triggered the fetch — toast/error fire only at request time, so a cached
// success is identical for both. On error the entry is evicted, so a follow-up caller
// refetches with its own suppression setting.
async function fetchSessionData(id: string, options: { suppressToast?: boolean } = {}): Promise<IPublicSession | null> {
  if (!id) return null;

  if (sessionDataCache.has(id)) {
    return sessionDataCache.get(id)!;
  }

  const sessionPromise = sendApiRequest<IPublicSession>(`/sessions/${id}`, {
    suppressToast: options.suppressToast,
  })
    .then((result) => result ?? null)
    .catch((error) => {
      sessionDataCache.delete(id);
      throw error;
    });

  sessionDataCache.set(id, sessionPromise);

  return sessionPromise;
}

export async function getSessionData(id: string): Promise<IPublicSession | null> {
  return fetchSessionData(id);
}

/**
 * Load session for the first time (e.g. when a customer opens a session link)
 */
export async function loadPublicSession(id: string): Promise<IPublicSession | null> {
  if (id === userSession().get('id')) {
    return userSession().get();
  }

  if (!isValidSessionID(id)) {
    throw new CustomerFacingError(EErrorCode.INVALID_SESSION_ID);
  }

  // Entering a DIFFERENT session — any master claim or release belongs to the previous one (AVS-4302).
  clearVoipClaim();
  clearVoipRelease();
  // A pending claim belongs to its own session (AVS-4461). A reload of the SAME session keeps it,
  // so the camera still waits for "Take over" — this runs on every fresh page, as does the line above.
  clearVoipClaimPendingOfOtherSession(id);
  // Same reasoning for the stream-host claim: this tab did not publish the incoming session's
  // stream id, so a claim left over from the previous one must not promote it (AVS-4452).
  clearStreamHost();
  // The version gate and dirty set are module-global. `seedRemoteVersion` below is monotonic, so
  // a previous session's higher lastSeenVersion would survive it and drop every new-session
  // broadcast as stale; stale dirty keys would wrongly shield the new session's remote values.
  // Reset both before loading the new record (AVS-4330).
  resetSessionSyncState();

  // Route loaders surface fetch errors via ErrorBoundary (customer) or the deleted-session
  // dialog (dealer). Suppress the global toast so a 404 doesn't render duplicate
  // "Sitzung wurde nicht gefunden" UI on top of the dedicated error surface.
  const sessionData = await fetchSessionData(id, { suppressToast: true });

  if (sessionData) {
    // Arm the staleness gate from the very first load, so a broadcast that arrives before this
    // client's first PUT is ordered instead of applied blind (AVS-4330 gate I5). `version` is a
    // server-set internal field not on IPublicSession, so it is read off the raw response.
    seedRemoteVersion((sessionData as { version?: number }).version);
    userSession().setValues({
      ...sessionData,
    });
    reassertRuntimeShowroomConfigId();
  }

  userSession().markRequirementReady('stateSynced');

  return sessionData as IPublicSession;
}

/**
 * If exists, returns the tenant config from the global E3D object.
 */
export function runtimeTenantConfig(): Partial<ITenantDefaults> {
  const tenantDefaults = (globalThis.E3D as unknown as E3DRuntime | undefined)?.tenantDefaults;
  if (!tenantDefaults || !isObject(tenantDefaults)) {
    return {};
  }
  return tenantDefaults;
}

/**
 * Re-applies the page's `E3D.init({ showroomConfigId })` value on top of a freshly loaded session
 * (AVS-4477).
 *
 * "Latest init wins": the integrator's current page decides the Showroom config-id, even when the
 * dealer resumes a session that stored a different one. `loadPublicSession` overwrites the
 * session with the stored record, so the runtime value has to be written back afterwards.
 *
 * Dealer only, and web-component only in effect:
 * - Standalone has no `globalThis.E3D`, so `runtimeTenantConfig()` is `{}` and there is nothing
 *   to re-assert; the stored value stays (accepted: a standalone dealer resuming a DCC session
 *   shows the DCC config).
 * - A customer never owns this key (`customerSessionSchema` omits it); it follows the dealer's
 *   value from the session, so a customer's own page must not overwrite it.
 *
 * This MUST be a separate `setValues` call from the load write, never merged into it.
 * `syncToServer` skips any emission that contains `id` (`skip:lifecycle-key`), so a merged write
 * would never be dirty-marked or PUT to the server and customers would keep the stale value.
 */
export function reassertRuntimeShowroomConfigId(): void {
  if (!user.isDealer) {
    return;
  }
  const showroomConfigId = runtimeTenantConfig().showroomConfigId;
  if (typeof showroomConfigId !== 'string') {
    return;
  }
  userSession().setValues({ showroomConfigId });
}

/**
 * Get default car selection method based on tenant configuration.
 * Safe to call after module initialization when tenantConfig is available.
 */
export function getDefaultCarSelectionMethod(): TCarSelectionMethod | '' {
  const config = tenantConfig();

  // Priority order: code/vin > carline > dcc
  if (config.isAllowed('selectionCode') || config.isAllowed('selectionVin')) {
    return 'code';
  }
  if (config.isAllowed('selectionCarline')) {
    return 'carline';
  }
  if (config.isAllowed('selectionDCC') || config.isAllowed('selectionSCC')) {
    return 'dcc';
  }
  return '';
}

export function isSessionActive(session: IPublicSession) {
  // Check if the session is not ended and has a valid ID
  return !!session.id && !session.endedAt && isValidSessionID(session.id);
}

export function isSessionPreparedButNotStarted(session: IPublicSession) {
  // Check if the session has not been started but the user has already applied data to it
  return !session.id && !session.endedAt;
}

/**
 * Detects the "scheduling-deleted" fingerprint: a session created with
 * sharingType='scheduled' (see src/state/session/schedule.ts) that was later deleted
 * via the schedule DELETE lambda — which sets `endedAt` and removes
 * `scheduledStartTime` via SessionService.removeAttribute (src/lambda/schedule/schedule.ts:438).
 *
 * NARROWING vs `sessionAbandoned`: the abandoned-fingerprint
 * (`endedAt && !isShared && !scheduledStartTime`) also matches regular dealer sessions
 * that ended without ever being shared. We discriminate via `sharingType === 'scheduled'`,
 * which the DELETE lambda preserves (it removes only `scheduledStartTime`). Without this
 * narrowing, the dealer loader would redirect every abandoned session to the
 * "Session unavailable" dialog instead of clearing state and going home.
 *
 * Intentionally does NOT include `streamActive`: server-side state can leave
 * streamActive=true on a scheduled session that was deleted before stream teardown
 * completed, and we still want to treat that as deleted.
 */
export function isSessionDeleted(session: IPublicSession | null | undefined) {
  if (!session) return false;
  return session.sharingType === 'scheduled' && !!session.endedAt && !session.scheduledStartTime && !session.isShared;
}

/**
 * Check if the current user is the session organizer
 * The organizer is the dealer who created the session, identified by matching userPseudonym
 */
export function isSessionOrganizer(): boolean {
  const session = userSession().get();
  return user.isDealer && user.userPseudonym === session.userPseudonym;
}

export type TSessionHostRole = 'host' | 'inherited' | 'not-organizer' | 'joining-live-stream';

/**
 * Determine whether this browsing context should act as the 3D stream host.
 *
 * Branch order is significant:
 * 1. Inherited (durable per-tab fact) outranks the joining-live-stream heuristic,
 *    which only becomes true once some host connects.
 * 2. Non-organiser (wrong/absent pseudonym) is checked before the stream heuristic
 *    so a second dealer never self-promotes into a host role.
 */
export function getSessionHostRole(): TSessionHostRole {
  const session = userSession().get();
  if (isInheritedSession(session.id)) return 'inherited';
  if (!isSessionOrganizer()) return 'not-organizer';
  // This context's own deck published the stream id, so it is the host no matter what the
  // heuristic below would infer. Without this the organizer is demoted to a joiner every time
  // a fresh React root re-runs the check mid-session, which is AVS-4452.
  if (isStreamHostForSession(session.id)) return 'host';
  if (userSession().hasStreamingId && session.streamActive) return 'joining-live-stream';
  return 'host';
}

/**
 * AVS-4482: with the VOIP election on, the dealer's master slot picks the one tab that hosts the 3D
 * stream — the tab the dealer is using — over the inherited and joiner rules; a standby, or a claim
 * still waiting for "Take over", never hosts.
 * `fallback` is the latched `getSessionHostRole()` answer for everything the election does not cover.
 */
export function resolveStreamHost(fallback: boolean): boolean {
  const session = userSession();
  // A standby, or a claim that waits for "Take over" (AVS-4461), stays receive-only and never hosts.
  if (session.isVoipWithoutOwnMedia) return false;
  // `amIMaster` is not flag-guarded: a slot stored before the flag turned off must not make a second host.
  if (session.isVoipSingleStreamEnabled && session.amIMaster && isSessionOrganizer()) return true;
  return fallback;
}

export interface IStartNewSessionOptions {
  /**
   * Optional initial data to merge into the new session.
   * Will be merged with default session values and user information.
   */
  initialData?: Partial<IPublicSession>;
  /**
   * When true, bypasses the active session check and creates a new session even if one is currently active.
   * Use this when you need to forcefully replace an existing session (e.g., when a non-organizer dealer
   * wants to leave a shared session and start their own).
   *
   * @default false
   */
  replaceActiveSession?: boolean;
}

/**
 * Fields a live session owns. They sync to every peer and re-key tenant config, so a host
 * remount (DCC car change calls `startSession()` with the runtime's values) must not overwrite
 * them (AVS-4461).
 */
const LIVE_SESSION_OWN_KEYS: ReadonlySet<string> = new Set<keyof IPublicSession>([
  'partnerToken',
  'locale',
  'sharingUri',
]);

/**
 * Creates a new session or resumes an existing active session.
 *
 * This function intelligently handles session creation based on the current session state:
 * - If an active session exists and `replaceActiveSession` is false, it returns the existing session
 * - If no active session exists, it creates a new one and registers it with the server
 * - If `replaceActiveSession` is true, it always creates a new session regardless of existing state
 *
 * The new session reuses brand, market, and locale from the previous session while resetting
 * critical session data like ID, timestamps, and streaming state.
 *
 * @param options - Configuration options for session creation
 * @returns Promise resolving to the new or existing session data
 *
 * @example
 * // Create a new session with default settings
 * const session = await startNewSession();
 *
 * @example
 * // Create a new session with initial data
 * const session = await startNewSession({
 *   initialData: { carDisplayName: 'Audi e-tron GT' }
 * });
 *
 * @example
 * // Force create a new session, replacing any active session
 * const session = await startNewSession({
 *   replaceActiveSession: true
 * });
 */
export async function startNewSession({ initialData, replaceActiveSession }: IStartNewSessionOptions = {}) {
  const existingSession = userSession().get();
  if (!replaceActiveSession && isSessionActive(existingSession)) {
    if (initialData) {
      const liveSessionData: Partial<IPublicSession> = Object.fromEntries(
        Object.entries(initialData).filter(([key]) => !LIVE_SESSION_OWN_KEYS.has(key))
      );
      userSession().setValues(liveSessionData);
    }
    userSession().emit('session:start', userSession().get());
    return userSession().get();
  }

  // if session was ended previously then we need to reset. Otherwise just continue
  const sessionData = isSessionPreparedButNotStarted(existingSession)
    ? { ...initialData }
    : getNewSessionData(initialData);
  const newSession = await registerSessionOnServer({
    ...sessionData,
  });

  if (!newSession?.id) {
    return;
  }
  // Fresh session — a claim carried over from a previous one must not outrank its masters.
  clearVoipClaim();
  clearVoipRelease();
  clearVoipClaimPendingOfOtherSession(newSession.id);
  // A context that creates its own session is its host — clear any inherited-session record.
  clearInheritedSession();
  // The previous session's stream-host claim must not follow the dealer into this one.
  clearStreamHost();
  // Same reason as loadPublicSession: clear the module-global version gate + dirty set so the
  // fresh session is not gated by the previous session's higher version (AVS-4330).
  resetSessionSyncState();
  userSession().emit('session:start', newSession);

  return newSession;
}

function sessionInitializationDefaults() {
  return userSessionSchema.parse({
    id: undefined,
    aveSessionId: '', // Initialize aveSessionId with a default empty string
    aveChannelName: '', // Initialize aveChannelName with a default empty string
    streamActive: false,
    timestamp: Date.now(),
    endedAt: undefined,
    wltpText: '',
    carDisplayName: '',
    carStatus: 'unset',
    syncStarted: false,
    activeExperience: '2D',
    scheduledStartTime: undefined,
    snapshot: '',
    offline: false,
    carSelectionMethod: '', // Default will be determined via getDefaultCarSelectionMethod() / session events
  });
}

export function getNewSessionData(initialData: Partial<IPublicSession> = {}): Partial<IPublicSession> {
  const runtimeDefaults = runtimeTenantConfig();

  const previousSession = { ...userSession().get() };

  // Reuse the previous session's brand, market, locale
  const brand = previousSession.brand;
  const market = previousSession.market;
  const locale = previousSession.locale;

  // Get default carSelectionMethod if not provided in initialData
  const carSelectionMethod = initialData.carSelectionMethod ?? getDefaultCarSelectionMethod();

  return userSessionSchema.parse({
    ...sessionInitializationDefaults(),
    brand,
    market,
    locale,
    userPseudonym: user.userPseudonym,
    dealerId: user.dealerId,
    dealerName: user.name,
    dealerEmail: user.email,
    dealerPhone: user.phoneNumber,
    dealerPhoto: user.publicPicture,
    ...runtimeDefaults,
    ...initialData,
    carSelectionMethod,
  });
}

export function clearSession() {
  broadcastingConnection?.disconnect();
  voipRtcConnection?.destroy();
  resetLaunchedConfigurator();
}

export function resetSession() {
  clearInheritedSession();
  clearStreamHost();
  userSession().setValues(getNewSessionData(), { reset: true });
}

/**
 * Options for ending a session.
 */
export interface IEndSessionOptions {
  /**
   * Whether to keep the HTTP connection alive after ending.
   * Useful for cleanup operations that should complete before connection closes.
   */
  keepAlive?: boolean;
  /**
   * Whether the session ended due to inactivity timeout.
   */
  isTimeout?: boolean;
  /**
   * Optional reason code or message for why the session ended.
   * Used for analytics and debugging.
   */
  reason?: string;
  /**
   * Whether the session is ending as part of a logout flow.
   * When true, local state updates won't emit events to prevent unwanted redirects.
   */
  isLogout?: boolean;
  /**
   * When true, only updates local state without broadcasting the end to other participants.
   * This allows non-organizer dealers to leave a shared session while keeping it active for others.
   * The session organizer can also use this to leave without ending the session for everyone.
   *
   * @default false
   */
  leaveWithoutEnding?: boolean;
}

/**
 * Ends the current session or allows a dealer to leave without ending it for others.
 *
 * This function handles two scenarios:
 * 1. **Session Organizer**: Can fully end the session for all participants (default behavior)
 *    or leave without ending it (when `leaveWithoutEnding` is true)
 * 2. **Non-Organizer Dealer**: Can only leave the session locally without affecting others
 *    (must use `leaveWithoutEnding: true`)
 *
 * When ending a session (organizer, `leaveWithoutEnding: false`):
 * - Stops broadcast streaming if active
 * - Sends analytics events
 * - Updates the server with ended session state
 * - Updates local state and emits session end events
 *
 * When leaving without ending (`leaveWithoutEnding: true`):
 * - Only updates local state
 * - Does not stop broadcasts or notify other participants
 * - Does not send analytics or server updates
 *
 * @param options - Configuration options for ending the session
 *
 * @example
 * // Organizer fully ends the session for everyone
 * await endSession();
 *
 * @example
 * // Non-organizer dealer leaves without ending for others
 * await endSession({ leaveWithoutEnding: true });
 *
 * @example
 * // Session timeout
 * await endSession({ isTimeout: true, reason: 'inactivity' });
 */
export async function endSession({
  isTimeout = false,
  keepAlive,
  reason,
  isLogout = false,
  leaveWithoutEnding = false,
}: IEndSessionOptions = {}): Promise<void> {
  // Early return: Only dealers can interact with sessions
  if (!user.isDealer) {
    logger.warn('Only dealers can end or leave sessions');
    return;
  }

  const isOrganizer = isSessionOrganizer();

  // Permission check: Non-organizers can only leave, not end for everyone
  if (!isOrganizer && !leaveWithoutEnding) {
    const { showToast } = await import('state/toast');
    showToast({
      type: 'warn',
      title: t`Cannot end session`,
      message: t`Only the session organizer can end this session. You can leave without ending it for others.`,
      notificationId: 'WARN_NOT_SESSION_ORGANIZER',
    });
    return;
  }

  const session = userSession().get();
  const snapshot = sharingSnapshot();

  // The session is over for this tab either way (end or leave) — forget the VOIP master
  // claim so a stale gen can never re-assert mastership into a later session (AVS-4302).
  clearVoipClaim();
  clearVoipRelease();
  clearVoipClaimPending();
  // Same for the stream-host claim. Without this, a dealer who leaves a session and later
  // returns to it is promoted to host by the stale claim while another context is driving
  // the live deck, which strands everyone on the next 3D->2D->3D flip (AVS-4452).
  clearStreamHost();

  // Stop broadcast streaming (only when fully ending the session)
  if (snapshot.sharingType === 'broadcast' && !leaveWithoutEnding) {
    stopBroadcast({ keepAlive });
  }

  // Prepare the ended session state
  const endedSessionData: IPublicSession = {
    ...session,
    streamActive: false,
    aveSessionId: '',
    aveChannelName: '',
    aveClientId: '',
    endedAt: Date.now(),
    carStatus: 'unset' as const,
    wltpText: '',
    activeVehicleId: undefined,
    hasTimedOut: isTimeout,
    // Local AVE has no keepalive/TTL and no `stopBroadcast`-equivalent release call — unlike
    // 'broadcast', nothing else deterministically disconnects it on session end. Clearing these
    // here (single setValues below) flips `isLocalAveSession`, which is what makes the Showroom
    // effect call `setConnection(null)` — race-proof against DCC's immediate session restart.
    ...(snapshot.sharingType === 'broadcast-local' && !leaveWithoutEnding
      ? { streamingDevice: '', renderer: 'cloud' as const, isShared: false, localAveTakenOver: false }
      : {}),
  };

  // Send analytics and update server (only when fully ending, not just leaving)
  if (!leaveWithoutEnding) {
    if (session.isShared) {
      sendSessionAnalyticsEvent(ESessionEvents.SESSION_ENDED, endedSessionData, keepAlive);
    }

    updatePublicSession.cancel();
    updatePublicSessionNow({ keepAlive, data: endedSessionData });
  }

  // Update local session state
  // When logging out, suppress events to prevent unwanted redirects
  userSession().setValues(endedSessionData, { emit: !isLogout });

  // Do not emit events during logout — prevents star-rating modal and other
  // post-session UI from appearing while the browser is navigating to the IdP
  if (!isLogout) {
    if (isTimeout) {
      userSession().emit('session:timeout', userSession().get());
    }
    userSession().emit('session:end', userSession().get(), reason);
  }

  if (leaveWithoutEnding) {
    // make sure we are not syncing the session with the server
    updatePublicSession.cancel();
  }
}

export function getSharingUri() {
  const session = userSession().get();
  const runtimeDefaults = runtimeTenantConfig();
  // Use the sharingUri from the session if available, otherwise fallback to runtime defaults or default streaming URI
  // or finally the current window location
  return session.sharingUri || runtimeDefaults.sharingUri || getStageOrigin() || window.location.origin;
}

export function getLinks(sessionData?: IPublicSession) {
  const session = sessionData || userSession().get();
  const origin = getSharingUri();
  const linkPrefix = `${origin}/${session.brand}/${session.market}`;
  const customerStreamUrl = `${linkPrefix}/view/${session.id}`;
  const universalStreamUrl = `${origin}/${session.id}`;

  return {
    customerStreamUrl: safeUrl(customerStreamUrl),
    universalStreamUrl: safeUrl(universalStreamUrl),
  };
}

export function updateSessionAndEmitAnalyticsEvent(updatedValue: Partial<IPublicSession>) {
  if (updatedValue) userSession().setValues(updatedValue);
  userSession().emit('change:analytics', updatedValue);
}

export function customerCanJoinRemote() {
  const session = userSession().get();
  return session.customerAllowed && session.sharingType === 'remote' && !!session.aveSessionId;
}

/**
 * Check if the session is ready for sharing.
 * A session is considered ready for sharing if:
 * - It has a valid 3D Exhibition session ID (created on the server)
 * - The session has not ended
 */
export function isSessionReadyForSharing(): boolean {
  const session = userSession().get();
  return !!session.id && !!session.prString;
}

/**
 * Check if the session is ready for sharing.
 * Dealer can start sharing if:
 * - The session is ready for sharing
 * - If local rendering is available then sharing can start without an aveSessionId
 * - If local rendering is not available then sharing can start only if aveSessionId is present and car is loaded
 *
 * This async version will auto-create a session if one doesn't exist and streaming isn't required.
 * For synchronous checks, use userSession().canStartSharing getter.
 */
export async function dealerCanStartSharing() {
  if (!user.isDealer) {
    return false;
  }

  const session = userSession().get();
  const isLocalRenderingAvailable = tenantConfig().isAllowed('sharingBroadcastLocal');
  const streamIsRequired = !isLocalRenderingAvailable;

  // Auto-create session if streaming is not required and no session exists
  if (!streamIsRequired && !session.id) {
    await startNewSession();
  }

  // Use the computed property for the actual check
  return userSession().canStartSharing;
}

export async function requiresStreamingBeforeSharing() {
  const isLocalRenderingAvailable = tenantConfig().isAllowed('sharingBroadcastLocal');

  const streamRequired = !isLocalRenderingAvailable;

  return streamRequired;
}

export interface IShareOptions {
  activeExperience?: EExperience;
  connectionCallback?: (session: IPublicSession) => Promise<boolean>;
}

// AVS-4389: emitted by share() when a confirmed device transition switches the local AVE
// device (not merely reselects the same one) — consumed by useLocalAveDeviceSwitch.
export const LOCAL_AVE_SWITCH_EVENT = 'session:local-ave-switch';
/**
 * Make any changes that should happen when the session is about to be shared.
 * - validation
 * - start sharing broadcast
 * - set the session snapshot
 * @returns true if the session is ready for sharing
 */
let shareInFlight = false;

export function isShareInFlight(): boolean {
  return shareInFlight;
}

export async function share({ activeExperience = '3D', connectionCallback }: IShareOptions = {}) {
  shareInFlight = true;
  try {
    if (!user.isDealer) {
      return false;
    }

    const session = userSession().get();

    // AVS-4354: the local renderer can never be started from Car Comparison (comparison is
    // 2D-only; LocalAVE renders only 3D). The UI already disables the option and the start CTA,
    // but share() is also public runtime API (E3D.share, reclaimLocalAveDevice) AND its awaits
    // below — two confirm dialogs plus the integrator connectionCallback — can sit open for
    // minutes while comparisonMode flips via cross-tab sync. So the invariant is enforced here,
    // on LIVE state, at entry and again after every await window (see re-checks below).
    const blockedByComparison = () =>
      isSharingOptionBlockedByComparison(userSession().get('sharingType'), userSession().get('comparisonMode'));
    if (blockedByComparison()) {
      return false;
    }

    // Declared here (not inside the `if` below) — the commit queue that reads it lives outside that block.
    let isLocalAveDeviceSwitch = false;

    // Let's check if the user is overriding anything in the local AVE and broadcast scenario
    if (session.sharingType === 'broadcast-local' || session.sharingType === 'broadcast') {
      const desiredStreamer = jsonSafeParse<models.AveDevice>(session.streamingDevice);
      const runningSession = sharingSnapshot();
      const runningStreamer = jsonSafeParse<models.AveDevice>(runningSession.streamingDevice);

      // Same comparison the transition dialog gates on below, narrowed to broadcast-local —
      // authorises the commit-time aveChannelName clear and the switch event further down.
      isLocalAveDeviceSwitch =
        session.sharingType === 'broadcast-local' &&
        !!runningStreamer.id &&
        !!desiredStreamer.id &&
        desiredStreamer.id !== runningStreamer.id;

      if (runningStreamer.id && desiredStreamer.id !== runningStreamer.id) {
        const response = await showConfirmDialog({
          dialogId: 'DIALOG_DEVICE_TRANSITION',
          title: t`Device Transition`,
          message: t`You previously shared on another device. Do you want to transition to a new one?`,
          confirmText: t`Yes, transition`,
          cancelText: t`Cancel`,
          'data-testid': 'device-transition-dialog',
        });
        if (!response) {
          return false;
        }
      }

      // A binding that already equals this tab's own localAveTabId is a no-op reconnect, not a
      // takeover of someone else's device — this happens routinely while the AVE is still in
      // its post-disconnect freeing window.
      if (desiredStreamer.sessionId && !isOwnDeviceBinding(desiredStreamer)) {
        const response = await showConfirmDialog({
          dialogId: 'DIALOG_SESSION_OVERRIDE',
          title: t`Override Session`,
          message: t`The selected local device is already in use. Do you want to continue and override the existing session?`,
          confirmText: t`Yes, override`,
          cancelText: t`Cancel`,
        });
        if (!response) {
          return false;
        }
      }
    }

    // Re-check after the dialog awaits, BEFORE the integrator callback: a share that is
    // already doomed must not trigger integrator-side work (LCC prepares connections and
    // sets aveSessionId inside connectionCallback).
    if (blockedByComparison()) {
      return false;
    }

    const resolvedCallback = connectionCallback ?? getGlobalConnectionCallback();
    if (resolvedCallback) {
      const response = await resolvedCallback(session);
      if (response === false) {
        return false;
      }
    }

    // Final re-check after the last await, immediately before the commit block — nothing
    // below this line awaits until the queue is applied, so a pass here cannot go stale
    // before isShared is set.
    if (blockedByComparison()) {
      return false;
    }

    if (session.id) {
      initSessionBroadcasting();

      if (
        !tenantConfig().isAllowed('voip') || // if the waiting room is not enabled then the customer view should be allowed
        session.sharingType !== 'remote' // only remote can have a waiting room, all other sharing types allow customer view immediately
      ) {
        // we allow customer immediately for local rendering because the AVE may use the customer view
        userSession().queue({
          customerAllowed: true,
        });
      }

      if (session.sharingType === 'remote' && integrationType() === IntegrationType.WebComponent) {
        // if the session is remote and the integration type is web component, we go fullscreen
        userSession().queue({
          streamFullScreen: true,
        });
      }

      // AVS-4302: the originating remote tab claims the single VOIP master slot
      // atomically with isShared, so it deterministically wins over a later-evaluating
      // tab (e.g. an inherited DCC tab). Claim ONLY when remote (NOT broadcast-local,
      // unlike aveClientId — GATE W4), the single-stream flag is on, and the slot is
      // empty or already ours — never overwrite a different dealer's master.
      // AVS-4454: voipMasters carries the per-session digest, never the raw signaling selector.
      const myVoipSlotId = getVoipSlotId(userSession().get('id'));
      // Re-read from live state (NOT the pre-callback `session` snapshot): another tab
      // may have claimed the slot via the session broadcast during the awaited
      // connectionCallback above, and the empty-or-mine guard must respect that.
      // Read-modify-write: the empty-or-mine guard is PER DEALER (keyed by pseudonym), so a
      // FOREIGN dealer's entry must NOT block my claim, and the spread below preserves it.
      const masters = userSession().get('voipMasters') ?? {};
      const mine = masters[user.userPseudonym];
      const canClaimVoipMaster =
        session.sharingType === 'remote' &&
        // Mirror `isVoipSingleStreamEnabled` / `claimVoipMasterIfVacant`: BOTH `voip` and
        // `voipSingleStream` must be on, else we persist a stale master on a voip-off tenant.
        tenantConfig().isAllowed('voip') &&
        tenantConfig().isAllowed('voipSingleStream') &&
        (!voipMasterTabId(mine) || mine?.tabId === myVoipSlotId);
      // Every claim site carries a gen and records it synchronously (see voipClaim.ts) so a
      // crossed same-dealer claim resolves deterministically instead of double-stepping-down.
      const shareClaimGen = nextVoipClaimGen(mine);
      if (canClaimVoipMaster) registerVoipClaim(shareClaimGen);

      userSession().queue({
        isShared: true,
        activeExperience: activeExperience,
        streamActive: true,
        endedAt: undefined,
        snapshot: JSON.stringify(userSession().get()),
        // Claim local AVE ownership atomically with isShared to prevent race conditions
        // between tabs. Only the tab that calls share() gets the aveClientId.
        ...(session.sharingType === 'broadcast-local' && session.streamingDevice
          ? { aveClientId: localAveTabId, localAveTakenOver: false }
          : {}),
        // Queue (not setValues) — a separate setValues before this would bake aveChannelName: ''
        // into the snapshot above; queuing keeps the clear in the SAME change event as isShared.
        ...(isLocalAveDeviceSwitch ? { aveChannelName: '' } : {}),
        ...(canClaimVoipMaster
          ? { voipMasters: { ...masters, [user.userPseudonym]: { tabId: myVoipSlotId, gen: shareClaimGen } } }
          : {}),
      });
    }

    userSession().applyQueue();

    // Cancel the pending debounced update (rejects its awaiters with an internally-handled
    // CancelledError), then write immediately — share() needs a guaranteed server update now.
    updatePublicSession.cancel();
    await updatePublicSessionNow();

    // AVS-4389: emit only AFTER the server write succeeds — a rejected write must not report
    // a switch as running while it never actually started (PR review, AVS-4389). Read the
    // device id from LIVE state, not the entry-time `session` snapshot above: the awaits this
    // function sits behind (two confirm dialogs plus connectionCallback) can leave `session`
    // stale by the time we get here, and the hook's `payload.deviceId !== currentLocalAveName`
    // guard would otherwise silently drop the event.
    if (isLocalAveDeviceSwitch) {
      userSession().emit(LOCAL_AVE_SWITCH_EVENT, {
        deviceId: jsonSafeParse<models.AveDevice>(userSession().get('streamingDevice')).id,
      });
    }

    // Start broadcasting to the NERD device AFTER the full session (including aveSessionId set
    // by integrators like LCC via connectionCallback) has been persisted to the server.
    // This ensures the NERD device loads the session with aveSessionId already in the DB,
    // preventing an indefinite hang on the "Waiting for your 3D session to start" screen.
    if (session.sharingType === 'broadcast') {
      startBroadcast();
    }

    userSession().emit('session:shared', userSession().get());
    return true;
  } finally {
    shareInFlight = false;
  }
}

let reclaimInFlight = false;

/**
 * Reclaims a taken-over LocalAVE device by re-running share() for 3D. Centralizes
 * error handling and reentrancy guarding needed by every UI entry point (AVS-4362).
 */
export async function reclaimLocalAveDevice(
  connectionCallback?: IShareOptions['connectionCallback']
): Promise<boolean> {
  if (reclaimInFlight) return false;
  reclaimInFlight = true;
  try {
    return await share({ activeExperience: '3D', connectionCallback });
  } catch (error) {
    console.error('Failed to reclaim local AVE device:', error);
    return false;
  } finally {
    reclaimInFlight = false;
  }
}

/**
 * Get the state that was frozen when session sharing started.
 */
export function sharingSnapshot(): IPublicSession {
  const serializedSharedState = userSession().get('snapshot') ?? '{}';
  return jsonSafeParse<IPublicSession>(serializedSharedState);
}

/** Make sure we sync only the values in the snapshot which have been cleared from the session (e.g. streamingDevice) */
export function syncSnapshot(): void {
  const session = userSession().get();
  if (!session.id || session.sharingType !== 'broadcast') {
    console.error('Session is not a broadcast session or does not have a valid ID. Cannot sync snapshot.');
    return;
  }

  const snapshot = sharingSnapshot();
  const sessionData = {
    ...session,
    ...Object.keys(snapshot).reduce<Record<string, unknown>>((acc, key) => {
      const typedKey = key as keyof IPublicSession;
      if (snapshot[typedKey] && !session[typedKey]) {
        acc[key] = snapshot[typedKey];
      }
      return acc;
    }, {}),
  };
  updatePublicSessionNow({
    data: sessionData,
    keepAlive: true,
  });
}

export function isVoipEnabled() {
  const session = userSession().get();
  return session.isShared && session.sharingType === 'remote' && session.customerAllowed;
}

/**
 * Reconciles the stored streamingDevice binding with live registry data after a takeover.
 * When the device was taken over (isLocalAveDeviceLost), the stored JSON may have a stale
 * sessionId/available. This updates it to match the live entry so share() reads fresh data.
 */
export function reconcileLostDeviceBinding(liveDevices: DeviceLike[]): void {
  if (isShareInFlight()) return;
  if (!userSession().isLocalAveDeviceLost) return;

  const bound = jsonSafeParse<models.AveDevice>(userSession().get('streamingDevice') ?? '');
  if (!bound.id) return;

  const live = liveDevices.find((d) => d.id === bound.id);
  if (!live) return;

  if (isBindingStale(bound, live)) {
    userSession().setValues({ streamingDevice: JSON.stringify(live) });
  }
}

/**
 * AVS-4302: fallback master claim for a dealer tab that reaches the VOIP publish gate
 * with ITS dealer's slot still vacant (e.g. the originating tab never ran share()
 * locally, or the claim has not yet broadcast). Mirrors the per-dealer empty-or-mine
 * guard in share() (GATE W4): claim ONLY for a dealer on a `remote` session with the
 * single-stream flag on and a vacant `voipMasters[myPseudonym]` slot. Read-modify-write
 * (spread the current map) so a DIFFERENT dealer's entry is preserved, and never
 * overwrites this dealer's existing master — that is a no-op. Flag-off, customer,
 * non-remote, or already-claimed ⇒ no-op (today's behaviour unchanged).
 */
export function claimVoipMasterIfVacant() {
  if (!user.isDealer) return;
  if (userSession().get('sharingType') !== 'remote') return;
  // Mirror `isVoipSingleStreamEnabled`: BOTH `voip` and `voipSingleStream` must be on.
  if (!tenantConfig().isAllowed('voip') || !tenantConfig().isAllowed('voipSingleStream')) return;

  const masters = userSession().get('voipMasters') ?? {};
  const slot = masters[user.userPseudonym];
  if (voipMasterTabId(slot)) return;

  logger.log('[VOIP] AVS-4302 master claim (vacant slot)', {
    tabId: getVoipTabId(),
    dealerPseudonym: user.userPseudonym,
  });
  // Record the claim gen synchronously — BEFORE any broadcast can cross — so a simultaneous
  // same-dealer claim resolves via reconcileVoipMasterClaim instead of both tabs stepping down.
  // The gen derives from the slot: after a release tombstone it must outrank it (AVS-4461).
  const claimGen = nextVoipClaimGen(slot);
  registerVoipClaim(claimGen);
  // AVS-4461: a claim from this tab's live connection built WITHOUT own media (a standby) is
  // pending: the tab owns the slot, but camera and mic start only on "Take over". Marked before
  // setValues, so every listener of the claim already reads it as pending.
  const sessionId = userSession().get('id');
  if (sessionId && voipRtcConnection?.sessionId === sessionId && voipRtcConnection.withoutOwnMedia) {
    setVoipClaimPending({ sessionId, gen: claimGen });
  }
  userSession().setValues({
    voipMasters: { ...masters, [user.userPseudonym]: { tabId: getVoipSlotId(userSession().get('id')), gen: claimGen } },
  });
  // `setValues` is local-only — without an explicit sync the claim never reaches the
  // server, so other same-dealer tabs never see the master and never become standbys
  // (multiple dealer streams on the wire, breaking AC b). The debounced sync suffices:
  // the customer-side dedup backstop covers the brief skew window before it lands.
  updatePublicSession();
}

/**
 * AVS-4302: reconcile this tab's master claim against the observed slot. Two same-dealer
 * tabs claim a vacant slot near-simultaneously (each tab's publish gate fires when the
 * OTHER's connection arrives) and the claims cross in transit — each tab then holds the
 * other's claim. Pre-fix, BOTH stepped down: both tabs showed the standby "Takeover" tile
 * and nobody published (the reported two-tab deadlock). The resolver picks exactly one
 * winner (higher gen, then lower tabId — see voipClaim.ts); the winner re-writes its claim
 * with an IMMEDIATE flush (like takeover, so the loser converges fast), the loser concedes.
 *
 * Returns true when the caller (View.tsx step-down effect) must step this tab down to
 * receive-only. Pure decision + claim write live here; the connection rebuild stays with
 * the caller, which owns the RTC lifecycle.
 */
export function reconcileVoipMasterClaim(): boolean {
  if (!userSession().isVoipSingleStreamEnabled) return false;
  const slot = userSession().get('voipMasters')?.[user.userPseudonym];

  // AVS-4461: a vacant (released) slot is claimed at once by a tab whose WORKING connection was
  // built without own media — a standby. Its claim is pending until the dealer clicks "Take over".
  // A tab built with media keeps today's claim path (the publish gate).
  if (!voipMasterTabId(slot)) {
    if (voipRtcConnection?.withoutOwnMedia && hasReadyVoipConnection(userSession().get('id'))) {
      claimVoipMasterIfVacant();
    }
    return false;
  }

  const action = resolveVoipClaimAction(slot, getVoipSlotId(userSession().get('id')));

  if (action === 'reassert') {
    const masters = userSession().get('voipMasters') ?? {};
    const myGen = getVoipClaimGen() ?? 0; // non-null after 'reassert' — the resolver kept our claim
    logger.log('[VOIP] AVS-4302 reasserting master claim over a stale/crossed write', {
      tabId: getVoipTabId(),
      dealerPseudonym: user.userPseudonym,
      slot,
    });
    userSession().setValues({
      voipMasters: { ...masters, [user.userPseudonym]: { tabId: getVoipSlotId(userSession().get('id')), gen: myGen } },
    });
    updatePublicSession.cancel();
    void updatePublicSessionNow();
    return false;
  }

  return action === 'stepDown';
}

/**
 * AVS-4461: free this tab's VOIP master slot after an involuntary teardown (the Stream view
 * destroyed the connection because VOIP turned off under a live share), so a standby can take
 * over. Writes a tombstone `{ tabId: '', gen: holderGen + 1 }` instead of deleting the slot: a
 * delayed occupied write for this tab then loses the gen-aware merge everywhere, and the
 * successor claims `holderGen + 2`. No flag guard — `amIMaster` ignores flags on purpose, and an
 * owner whose `voip` flag just turned off still holds the slot. A deliberate stop
 * (`E3D.destroyVoipConnection()`), a takeover, a step-down or a session end never releases.
 */
export function releaseVoipMaster(): void {
  if (!user.isDealer || !userSession().amIMaster) return;
  if (!isSessionActive(userSession().get()) || userSession().presentationCompleted) return;

  const masters = userSession().get('voipMasters') ?? {};
  const releaseGen = nextVoipClaimGen(masters[user.userPseudonym]);
  logger.log('[VOIP] AVS-4461 releasing master slot after an involuntary teardown', {
    tabId: getVoipTabId(),
    dealerPseudonym: user.userPseudonym,
    releaseGen,
  });
  clearVoipClaim();
  registerVoipRelease(releaseGen);
  clearVoipClaimPending();
  userSession().setValues({
    voipMasters: { ...masters, [user.userPseudonym]: { tabId: '', gen: releaseGen } },
  });
  updatePublicSession.cancel();
  void updatePublicSessionNow()?.catch((error: unknown) => {
    logger.warn('[VOIP] AVS-4461 release PUT failed; the dirty flush re-sends the tombstone', error);
  });
}

/**
 * AVS-4330: the gen-aware merge (applyRemote.ts) kept this tab's newer master claim over a
 * stale broadcast, so the last-writer-wins server may still hold the losing map — re-push
 * local state (budget-capped, shared with the reassert flap brake) so the server converges.
 */
export function repushVoipMasters(): void {
  if (!consumeReassertBudgetForRepair()) return;
  logger.log('[VOIP] AVS-4330 re-pushing defended master claim to repair server state', {
    tabId: getVoipTabId(),
    dealerPseudonym: user.userPseudonym,
  });
  updatePublicSession.cancel();
  void updatePublicSessionNow();
}

/**
 * AVS-4302 (Task 8): manual VOIP takeover (spec §5.4). A standby dealer tab clicks
 * "Take over" on its mirrored self tile to move publishing to THIS tab and step the
 * old master down. The camera MUST be acquired inside the user-gesture chain, so the
 * camera init is the FIRST await — only after it succeeds do we overwrite the master
 * slot and reconnect as publisher.
 *
 * Order:
 *  1. Acquire camera/mic. On failure ⇒ warn toast + abort (voipMaster unchanged, the
 *     old master keeps streaming — no half-takeover that leaves the call dealer-less).
 *  2. DIRECT overwrite of `voipMasters[myPseudonym]` to this tab (NOT
 *     claimVoipMasterIfVacant, which only fills a vacant slot — takeover must beat an
 *     existing master); read-modify-write preserves other dealers' entries + immediate
 *     flush (like share()) so the old master sees it and runs the step-down (View.tsx).
 *  3. Reconnect this tab as publisher: destroy() then initWebRtc(). With the camera now
 *     present and amIMaster true / isVoipStandby false, the camera grab + publish gate
 *     run (Task 9 makes destroy()→initWebRtc a clean, leak-free rebuild).
 *
 * Guard: only meaningful for a dealer on a remote session with both flags on, and only
 * when not already the master. Anything else is a no-op (today's behaviour unchanged).
 */
export function performVoipTakeover(): Promise<void> {
  // AVS-4461: one takeover at a time. A double click would claim twice and rebuild twice, and the
  // second destroy() would land on the client the first call just built.
  if (voipTakeoverInFlight) return voipTakeoverInFlight;
  voipTakeoverInFlight = runVoipTakeover().finally(() => {
    voipTakeoverInFlight = null;
  });
  return voipTakeoverInFlight;
}

let voipTakeoverInFlight: Promise<void> | null = null;

function canTakeOverVoip(): boolean {
  if (!user.isDealer) return false;
  if (userSession().get('sharingType') !== 'remote') return false;
  if (!tenantConfig().isAllowed('voip') || !tenantConfig().isAllowed('voipSingleStream')) return false;
  // Already publishing master — nothing to take over (the overlay never shows here, but
  // keep the public function safe regardless of caller). A master whose claim is pending its
  // media (AVS-4461) is not publishing yet: the click is its only way to start camera and mic.
  return !userSession().amIMaster || userSession().isVoipClaimPendingMedia;
}

async function runVoipTakeover(): Promise<void> {
  if (!canTakeOverVoip()) return;

  // Step 1 — acquire camera/mic in the gesture chain. Mirror initWebRtc's
  // stream:started / stream:error promise so we know whether the grab succeeded.
  const sessionAtStart = userSession().get('id');
  const cameraAcquiredHere = !myCameraStream().stream;
  const cameraReady = await acquireCameraInGesture();
  if (!cameraReady) {
    logger.log('[VOIP] AVS-4302 takeover aborted: camera/mic unavailable', {
      dealerPseudonym: user.userPseudonym,
    });
    const { showToast } = await import('state/toast');
    showToast({
      title: t`Camera and microphone unavailable`,
      message: t`Your camera and/or microphone are not allowed or not available, so you cannot take over the call. Update your browser permissions and reload the page.`,
      type: 'warn',
      notificationId: 'WARN_VOIP_TAKEOVER_NO_MEDIA',
    });
    return;
  }

  // AVS-4461: the permission prompt can stay open for minutes. If the call ended, VOIP went off or
  // the slot moved on meanwhile, nothing would own a camera grabbed for it: release it and stop.
  const stillApplies = canTakeOverVoip() && userSession().get('id') === sessionAtStart && !userSession().get('endedAt');
  if (!stillApplies) {
    logger.log('[VOIP] AVS-4461 takeover no longer applies after the camera prompt', {
      dealerPseudonym: user.userPseudonym,
    });
    if (cameraAcquiredHere) {
      myCameraStream().stopStream();
    }
    return;
  }

  // Step 2 — overwrite the master slot and flush immediately (not debounced) so the
  // old master tab receives the broadcast and steps down (View.tsx step-down effect).
  logger.log('[VOIP] AVS-4302 takeover: claiming master on this tab', {
    tabId: getVoipTabId(),
    dealerPseudonym: user.userPseudonym,
  });
  const masters = userSession().get('voipMasters') ?? {};
  // gen = holder-gen + 1: an intentional takeover outranks the incumbent's claim, so the
  // displaced master concedes (stepDown) and can never tie-break the slot back.
  const takeoverGen = nextVoipClaimGen(masters[user.userPseudonym]);
  registerVoipClaim(takeoverGen);
  // The camera is live now, so the claim is no longer pending (AVS-4461).
  clearVoipClaimPending();
  userSession().setValues({
    voipMasters: {
      ...masters,
      [user.userPseudonym]: { tabId: getVoipSlotId(userSession().get('id')), gen: takeoverGen },
    },
  });
  // The write above flips this tab out of standby SYNCHRONOUSLY, but the rebuild only happens
  // after the flush below — unsuppress the old instance now so remounting tiles play live
  // media instead of a black+silent flash for ~1 network round trip.
  voipRtcConnection?.liftIncomingMediaSuppression();
  updatePublicSession.cancel();
  // A failed flush must not strand the tab: the camera is live and the claim is set locally, and the
  // dirty flush re-sends it. Skipping the rebuild would leave the camera running unpublished.
  await updatePublicSessionNow()?.catch((error: unknown) => {
    logger.warn('[VOIP] AVS-4461 takeover flush failed; the dirty flush re-sends the claim', error);
  });

  // Step 3 — reconnect as publisher (clean rebuild; publish gate now passes).
  voipRtcConnection?.destroy();
  await initWebRtc(userSession().get('id'));
}

/**
 * Acquire the local camera/mic inside the current (user-gesture) call stack, resolving
 * `true` once the stream starts or `false` if the browser denies/fails it. Shared shape
 * with initWebRtc's receive-only fallback, but here a failure aborts the takeover.
 */
function acquireCameraInGesture(): Promise<boolean> {
  if (myCameraStream().stream) return Promise.resolve(true);

  return new Promise<boolean>((resolve) => {
    let unsubscribeStarted: (() => void) | null = null;
    let unsubscribeError: (() => void) | null = null;

    const cleanup = () => {
      unsubscribeStarted?.();
      unsubscribeError?.();
    };

    unsubscribeStarted = myCameraStream().once('stream:started', () => {
      cleanup();
      resolve(true);
    });
    unsubscribeError = myCameraStream().once('stream:error', () => {
      cleanup();
      resolve(false);
    });

    myCameraStream().init();
  });
}

export function streamCanStart() {
  const session = userSession().get();
  // The stream can start if a valid PR string exists and the stream is active
  return !!session.prString && session.streamActive && userSession().sessionReadyForStreaming;
}

type ShowCarOptions = {
  prString: TPrString;
  okapiCode?: string;
  name: string;
  wltpText?: string;
  carDetails?: ICarDetails;
  efficiencyClass?: string;
  // AVS-4481: VW/VWN model revision (MP1/MP2). Normalised in showCar; omitted/unknown becomes ''.
  modelRevision?: string;
  // VTP-specific fields (only populated when resolved via VTP API)
  isVtp?: boolean;
  vtpDetails?: IVtpDetails;
  vtpPhotos?: IVtpPhoto[];
};

// Function overloads allows legacy, prString-based calls - and new object-based (named) calls that can allow okapi code as well
export function showCar(prString: TPrString, name: string, wltpText?: string): void;
export function showCar(options: ShowCarOptions): void;
export function showCar(prStringOrOptions: TPrString | ShowCarOptions, name?: string, wltpText?: string): void {
  let prString: TPrString;
  let carDisplayName: string;
  // Deliberately NOT coerced to '' when the caller omits it (AVS-4400 review): the registry's
  // duplicate self-heal reads an explicitly empty regulatory value as "this car genuinely has
  // none" and clears a wrong one with it. A caller that simply does not know the WLTP — carline
  // selection, say — must stay "not supplied" and leave an existing value alone.
  let wltpTextValue: string | undefined;
  let okapiCode = '';
  let modelRevisionValue: string | undefined;
  let carDetailsValue: ICarDetails | undefined = undefined;
  let efficiencyClassValue: string | undefined = undefined;
  let isVtpValue: boolean | undefined = undefined;
  let vtpDetailsValue: ShowCarOptions['vtpDetails'] = undefined;
  let vtpPhotosValue: ShowCarOptions['vtpPhotos'] = undefined;

  // Handle object-based call
  if (typeof prStringOrOptions !== 'string' && isObject(prStringOrOptions) && 'name' in prStringOrOptions) {
    const options = prStringOrOptions as ShowCarOptions;
    prString = options.prString || '';
    okapiCode = options.okapiCode || '';
    modelRevisionValue = options.modelRevision;
    carDisplayName = options.name;
    wltpTextValue = options.wltpText;
    carDetailsValue = options.carDetails;
    efficiencyClassValue = options.efficiencyClass;
    isVtpValue = options.isVtp;
    vtpDetailsValue = options.vtpDetails;
    vtpPhotosValue = options.vtpPhotos;
  } else {
    prString = prStringOrOptions as TPrString;
    carDisplayName = name || '';
    wltpTextValue = wltpText;
  }

  addVehicle(
    {
      prString,
      carDisplayName,
      wltpText: wltpTextValue,
      vinOrMarketingCode: okapiCode, // if no okapiCode is provided, we reset the value. this helps when we switch the car during the session to avoid having stale data.
      // Always a string, so '' resets a stale revision when the car changes (like vinOrMarketingCode above).
      // Gated on the session brand: only VW/VWN support a model revision (AVS-4481 review).
      modelRevision: supportsModelRevision(userSession().get('brand'))
        ? normalizeModelRevision(modelRevisionValue)
        : '',
      carDetails: carDetailsValue,
      efficiencyClass: efficiencyClassValue,
      carStatus: 'ready',
      isVtp: isVtpValue,
      vtpDetails: vtpDetailsValue,
      vtpPhotos: vtpPhotosValue,
    },
    true
  );
}

/**
 * @returns true if the car is ready to be used for other actions (e.g. start streaming, show car details)
 */
export function carReady() {
  const session = userSession().get();
  return session.carStatus === 'ready';
}

/**
 * Check if the session is in the URL search params and load it if it is.
 * @returns session data if the session was loaded (via promise), false otherwise
 */
export function resumeSessionFromParams() {
  const sid = getCurrentUrlParameter('e3d-sid');
  if (sid) {
    // Record the hand-off synchronously, before the async server load, so the fact
    // is available for the first render cycle regardless of load timing.
    markSessionInherited(sid);
    return loadPublicSession(sid);
  }
  return false;
}

if (isDebugMode()) {
  // @ts-expect-error - expose config in global scope for debugging
  window.userSession = userSession;
}
